/**
 * Calculate linear regression (OLS) for trend analysis
 * @param {Array} data - Array of data points [{date, users}, ...]
 * @returns {Object} Trend analysis result
 */
export function calculateLinearRegression(data) {
  if (!data || data.length < 2) {
    return null;
  }

  const n = data.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  // Calculate sums for linear regression
  data.forEach((point, index) => {
    const x = index; // x is the index (day number)
    const y = point.users || 0;

    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  // Calculate slope and intercept using OLS formula
  // slope = (n*Σ(xy) - Σx*Σy) / (n*Σ(x²) - (Σx)²)
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

  // intercept = (Σy - slope*Σx) / n
  const intercept = (sumY - slope * sumX) / n;

  // Calculate percentage change based on TREND LINE (not raw data)
  // This ensures the % change sign always matches the slope sign
  const trendStart = intercept;
  const trendEnd = slope * (n - 1) + intercept;

  const percentChange = trendStart !== 0
    ? ((trendEnd - trendStart) / trendStart) * 100
    : 0;

  // Calculate absolute change based on trend line
  const netChange = trendEnd - trendStart;

  // Generate trend line points for visualization.
  // Clamp at 0 — the underlying metric (active users) cannot be negative,
  // so a regression line that dips below zero would be misleading.
  const trendLine = data.map((point, index) => ({
    date: point.date,
    trendValue: Math.max(0, slope * index + intercept)
  }));

  return {
    slope,
    intercept,
    trendLine,
    percentChange,
    netChange,
    absSlope: Math.abs(slope) // for sorting by absolute magnitude
  };
}
